import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleDot,
  faTriangleExclamation,
  faVials,
} from "@fortawesome/pro-regular-svg-icons";
import { CircularProgress, Tooltip } from "@mui/material";
import {
  AlertsPill,
  AlertsPillLabel,
  AlertsPillSections,
  AlertsPillSection,
  AlertsPillIcon,
  AlertsPillBadge,
  AlertsPillBadgeLoader,
} from "./CfcAlert.styles";
import { AlertTab } from "./CfcEligibilityModal";

interface AlertsButtonProps {
  actionsNeededCount: number;
  redrawsCount: number;
  unityEligibleCount: number;
  actionsNeededLoading?: boolean;
  redrawsLoading?: boolean;
  unityEligibleLoading?: boolean;
  onOpenTab: (tab: AlertTab) => void;
}

interface AlertSectionConfig {
  count: number;
  loading: boolean;
  icon: typeof faCircleDot;
  activeLabel: string;
  emptyLabel: string;
  tab: AlertTab;
}

const getAlertSections = ({
  actionsNeededCount,
  redrawsCount,
  unityEligibleCount,
  actionsNeededLoading = false,
  redrawsLoading = false,
  unityEligibleLoading = false,
}: Pick<
  AlertsButtonProps,
  | "actionsNeededCount"
  | "redrawsCount"
  | "unityEligibleCount"
  | "actionsNeededLoading"
  | "redrawsLoading"
  | "unityEligibleLoading"
>): AlertSectionConfig[] => [
  {
    count: unityEligibleCount,
    loading: unityEligibleLoading,
    icon: faCircleDot,
    activeLabel: "Eligible Unity Confirm patient(s)",
    emptyLabel: "No eligible Unity Confirm patient(s)",
    tab: AlertTab.UnityConfirmEligibility,
  },
  {
    count: redrawsCount,
    loading: redrawsLoading,
    icon: faVials,
    activeLabel: "Redraw request(s)",
    emptyLabel: "No redraws needed",
    tab: AlertTab.RedrawsRequests,
  },
  {
    count: actionsNeededCount,
    loading: actionsNeededLoading,
    icon: faTriangleExclamation,
    activeLabel: "Patient(s) needing action",
    emptyLabel: "No actions required",
    tab: AlertTab.ActionsNeeded,
  },
];

const getAlertSectionLabel = ({
  activeLabel,
  emptyLabel,
  isEmpty,
  loading,
}: {
  activeLabel: string;
  emptyLabel: string;
  isEmpty: boolean;
  loading: boolean;
}): string => {
  if (loading) {
    return `${activeLabel} loading`;
  }

  if (isEmpty) {
    return emptyLabel;
  }

  return activeLabel;
};

export const AlertsButton = ({
  actionsNeededCount,
  redrawsCount,
  unityEligibleCount,
  actionsNeededLoading = false,
  redrawsLoading = false,
  unityEligibleLoading = false,
  onOpenTab,
}: AlertsButtonProps): React.ReactElement => {
  const sections = getAlertSections({
    actionsNeededCount,
    redrawsCount,
    unityEligibleCount,
    actionsNeededLoading,
    redrawsLoading,
    unityEligibleLoading,
  });
  const allEmpty = sections.every(
    ({ count, loading }) => !loading && count === 0,
  );

  return (
    <AlertsPill role="group" aria-label="Alerts" $muted={allEmpty}>
      <AlertsPillLabel $muted={allEmpty}>Alerts</AlertsPillLabel>
      <AlertsPillSections>
        {sections.map(
          ({ count, loading, icon, activeLabel, emptyLabel, tab }) => {
            const isEmpty = count === 0;
            const label = getAlertSectionLabel({
              activeLabel,
              emptyLabel,
              isEmpty,
              loading,
            });
            return (
              <Tooltip key={activeLabel} arrow placement="top" title={label}>
                <span>
                  <AlertsPillSection
                    type="button"
                    aria-label={`${label}${isEmpty || loading ? "" : ` (${count})`}`}
                    aria-busy={loading || undefined}
                    disabled={loading || isEmpty}
                    onClick={() => onOpenTab(tab)}
                    $muted={!loading && isEmpty}
                  >
                    <AlertsPillIcon aria-hidden="true">
                      <FontAwesomeIcon icon={icon} fontSize={15} />
                    </AlertsPillIcon>
                    {loading ? (
                      <AlertsPillBadgeLoader aria-hidden="true">
                        <CircularProgress
                          color="inherit"
                          size={12}
                          thickness={5}
                        />
                      </AlertsPillBadgeLoader>
                    ) : (
                      <AlertsPillBadge>{count}</AlertsPillBadge>
                    )}
                  </AlertsPillSection>
                </span>
              </Tooltip>
            );
          },
        )}
      </AlertsPillSections>
    </AlertsPill>
  );
};
